import React, { useEffect, useMemo } from "react";
import { PropTypes } from "prop-types";
import { observer } from "mobx-react";
import { MrbPageHeader } from "mrb/components/layout";
import { MrbButton } from "mrb/components/button";
import {
    EditLoadioStoreFeaturedSectionContentModal,
    LoadioStoreFeaturedSectionLoader,
} from "administration/loadio/components";
import { MrbContentLoader } from "mrb/components/loader";
import { LoadioStoreFeaturedSectionItemViewStore } from "administration/loadio/stores";
import { DragDropList, UploadedImageLoader } from "common/components";
import { Image } from "common/components";
import { MrbFileUpload } from "mrb/components/media";

import { defaultTemplate } from "common/hoc";
import { isNil } from "lodash";
import { MrbPage } from "mrb";

function LoadioStoreFeaturedSectionTemplate({ loadioStoreFeaturedSectionViewStore, t }) {
    const { editItemModalParams, loaderStore } = loadioStoreFeaturedSectionViewStore;

    return (
        <React.Fragment>
            <MrbPageHeader>
                <div>Loadio Store - Featured</div>
                <Actions store={loadioStoreFeaturedSectionViewStore} t={t} />
            </MrbPageHeader>
            <MrbPage>
                <div className="c-container--full">
                    <MrbContentLoader
                        loaderStore={loaderStore}
                        loaderRender={() => <LoadioStoreFeaturedSectionLoader />}
                    >
                        {() => <ItemsList viewStore={loadioStoreFeaturedSectionViewStore} />}
                    </MrbContentLoader>
                </div>

                <EditLoadioStoreFeaturedSectionContentModal
                    modalParams={editItemModalParams}
                    loaderStore={loaderStore}
                />
            </MrbPage>
        </React.Fragment>
    );
}

const Actions = observer(({ store, t }) => {
    const { updateFeatured, isSubmitting } = store;

    return (
        <MrbButton
            t={t}
            className="c-btn c-btn--base c-btn--primary"
            onClick={updateFeatured}
            aria-label="Save"
            aria-busy={isSubmitting}
            label="Save"
            disabled={isSubmitting}
        />
    );
});

const ItemsList = observer(({ viewStore }) => {
    const {
        items,
        itemLimit,
        reorderItems,
        editItem,
        addItemModalFunction,
        removeItem,
        loaderStore,
        rootStore,
        routeStore,
    } = viewStore;

    return (
        <MrbContentLoader loaderStore={loaderStore}>
            {() => (
                <>
                    <AddItem onAdd={addItemModalFunction} items={items} limit={itemLimit} />
                    <DragDropList
                        droppableId="loadio-store-featured-section-items"
                        items={items}
                        onDragEnd={reorderItems}
                        direction="horizontal"
                        listClassName="c-grid c-grid--store-list"
                        itemRender={(item) => (
                            <ListItem
                                key={item.loadioArtistId || item.loadioAlbumId}
                                item={item}
                                editItem={editItem}
                                removeItem={removeItem}
                                rootStore={rootStore}
                                routeStore={routeStore}
                            />
                        )}
                    />
                </>
            )}
        </MrbContentLoader>
    );
});

function AddItem({ onAdd, items, limit }) {
    return (
        <div className="c-card--list-item">
            <div className="c-featured__card">
                <div className="c-featured__card__img--mid u-display--flex u-display--flex--ai-center">
                    <MrbButton
                        disabled={!isNil(items) && items.length >= limit}
                        label={`Add new item (${items?.length ?? 0}/${limit})`}
                        onClick={onAdd}
                    />
                </div>
            </div>
        </div>
    );
}

const ListItem = observer(({ item, editItem, removeItem, rootStore, routeStore }) => {
    const store = useMemo(
        () => new LoadioStoreFeaturedSectionItemViewStore(rootStore, { item, routeStore }),
        [item, rootStore, routeStore]
    );

    useEffect(() => {
        store.initialize();
    }, [store]);

    const width = 300;
    const height = 300;

    let coverImageUrl = null;
    if (item.fileEntryId) {
        coverImageUrl = rootStore.coverImageFactory.createFeaturedSectionCoverImageUrl(item.fileEntryId, width, height);
    } else if (item.loadioArtist && item.loadioArtist.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createLoadioArtistCoverImageUrl(
            item.loadioArtist.coverImageId,
            width,
            height
        );
    } else if (item.loadioAlbum && item.loadioAlbum.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createLoadioAlbumCoverImageUrl(
            item.loadioAlbum.coverImageId,
            width,
            height
        );
    }

    return (
        <div className="c-card--list-item">
            <div className="c-featured__card u-width--100">
                <div className="c-featured__card__img--mid flex-1 u-position--rel">
                    <div className="c-upload__actions">
                        <div className="c-upload__replace">
                            <MrbButton
                                label="Replace image"
                                onClick={store.uploadImage}
                                classNameOverride="c-btn c-btn--sml c-btn--primary"
                                icon="u-icon u-icon--base u-icon--sync u-mar--right--tny"
                            />
                        </div>

                        {(item.fileEntryId || store.coverImageUploadViewStore.file) && (
                            <MrbButton
                                aria-label="Revert to Default"
                                label="Clear"
                                onClick={() => store.revertToDefaultArtwork(item)}
                                classNameOverride="c-btn c-btn--sml c-btn--primary u-mar--left--tny c-upload__clear"
                                icon="u-icon u-icon--base u-icon--close u-mar--right--tny"
                            />
                        )}
                    </div>

                    <MrbContentLoader
                        loaderStore={store.coverImageUploadViewStore.loaderStore}
                        loaderRender={() => <UploadedImageLoader className="flex" customCardHeight={208} />}
                    >
                        {() => (
                            <div className="c-card--table u-height--100">
                                <MrbFileUpload store={store.coverImageUploadViewStore.fileUploadStore}>
                                    <Image src={store.coverImageUploadViewStore.imageUrl ?? coverImageUrl} />
                                </MrbFileUpload>
                            </div>
                        )}
                    </MrbContentLoader>
                </div>

                <ContentLabel item={item} />

                <div className="u-display--flex c-grid__gap--2 u-mar--top--micro">
                    <MrbButton label="Change" onClick={() => editItem(item)} classNameExtend="c-btn--full" />
                    <MrbButton
                        classNameOverride="c-btn c-btn--base c-btn--ghost c-btn--full"
                        label="Remove"
                        onClick={() => {
                            store.revertToDefaultArtwork();
                            removeItem(item);
                        }}
                    />
                </div>
            </div>
        </div>
    );
});

function ContentLabel({ item }) {
    let icon = null;
    let title = null;
    let isPremium = false;

    if (!isNil(item.loadioArtistId)) {
        icon = "loadio";
        title = item.loadioArtist?.name;
    } else if (!isNil(item.loadioAlbumId)) {
        icon = "loadio";
        title = item.loadioAlbum?.title;
    }

    return (
        <div className="flex items-center u-gap--sml u-mar--top--sml">
            <i className={`u-icon u-icon--med u-icon--${icon}`} />
            <p className="u-type--base u-type--ellipsis" title={title}>
                {title}
            </p>
            {isPremium && <i className="u-icon u-icon--med u-icon--premium" />}
        </div>
    );
}

LoadioStoreFeaturedSectionTemplate.propTypes = {
    loadioStoreFeaturedSectionViewStore: PropTypes.object.isRequired,
    t: PropTypes.func,
};

export default defaultTemplate(LoadioStoreFeaturedSectionTemplate);
